import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import { toast } from "sonner";
import { Barn, Trash, ArrowClockwise, Polygon, Printer } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import {
  listMyFields, createMyField, deleteMyField, fetchMyFieldSnapshot,
  fetchMyFieldGrassland, subscribeFieldAlert,
} from "@/lib/api";
import {
  regionHealth, healthBand, toneColor, ndviStatus, soilMoistureStatus, yieldStatus,
} from "@/lib/laymen";
import { enablePushForRegion } from "@/lib/alerts"; // reused url-base64 helper only
import { Fire, CheckCircle, Warning, EnvelopeSimple, Bell, PaperPlaneTilt, CircleNotch, Horse } from "@phosphor-icons/react";
import AddressSearch from "@/components/AddressSearch";

const CROPS = [
  { id: "wheat", label: "Getreide / Weizen" },
  { id: "corn",  label: "Mais / Hackfrucht" },
  { id: "forest_lake", label: "Wald / Gewässer" },
];

export default function MyFields() {
  const [fields, setFields] = useState([]);
  const [selected, setSelected] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    const list = await listMyFields();
    setFields(list);
    if (list.length && !selected) setSelected(list[0]);
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected) { setSnapshot(null); return; }
    (async () => {
      try { setSnapshot(await fetchMyFieldSnapshot(selected.id)); }
      catch { setSnapshot(null); }
    })();
  }, [selected]);

  const onSaved = async () => { await refresh(); };

  const onDelete = async (id) => {
    if (!window.confirm("Feld wirklich löschen?")) return;
    await deleteMyField(id);
    toast.success("Feld gelöscht");
    if (selected?.id === id) setSelected(null);
    refresh();
  };

  const onForceRefresh = async () => {
    if (!selected) return;
    setRefreshing(true);
    try {
      const s = await fetchMyFieldSnapshot(selected.id, true);
      setSnapshot(s);
      toast.success("Frisch geladen");
    } catch {
      toast.error("Aktualisierung fehlgeschlagen");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="myfields-root">
      <header>
        <div className="eid-label">Persönlich · Eigene Flächen</div>
        <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1 flex items-center gap-2">
          <Barn size={28} /> Mein Hof
        </h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Zeichne deine eigenen Felder oder Waldstücke ein und sieh für genau
          diese Fläche NDVI, Bodenfeuchte, Wetterprognose und Ertrags-Index.
          Daten werden 6 h gecached und auf Abruf aktualisiert.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DrawMap onSaved={onSaved} fields={fields} selectedId={selected?.id} onSelect={setSelected} />

        <aside className="space-y-3" data-testid="myfields-sidebar">
          {fields.length === 0 ? (
            <div className="eid-card p-6 text-sm text-zinc-400">
              Noch keine Felder. Links oben auf <Polygon size={12} className="inline" />
              <strong> Polygon zeichnen</strong> klicken und den Umriss deiner Fläche auf der Karte
              nachzeichnen. Doppelklick zum Abschließen.
            </div>
          ) : (
            <div className="eid-card divide-y divide-white/10" data-testid="myfields-list">
              {fields.map((f) => {
                const active = selected?.id === f.id;
                return (
                  <div key={f.id} className="flex items-center gap-2 p-3"
                       style={{ background: active ? "rgba(255,255,255,0.06)" : "transparent" }}
                       data-testid={`myfield-${f.id}`}>
                    <button
                      onClick={() => setSelected(f)}
                      className="flex-1 text-left"
                    >
                      <div className="text-sm font-medium">{f.label}</div>
                      <div className="text-[11px] font-mono text-zinc-500">
                        {CROPS.find((c) => c.id === f.crop)?.label || f.crop} · {f.area_km2} km²
                      </div>
                    </button>
                    <button
                      onClick={() => onDelete(f.id)}
                      className="text-zinc-500 hover:text-[#FF7A45] p-1"
                      aria-label="Löschen" data-testid={`myfield-delete-${f.id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {selected && (
            <FieldDetail snapshot={snapshot} onRefresh={onForceRefresh} refreshing={refreshing} field={selected} />
          )}
        </aside>
      </div>
    </div>
  );
}

function DrawMap({ onSaved, fields, selectedId, onSelect }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const drawnLayer = useRef(null);
  const ownedLayer = useRef(null);
  const [pending, setPending] = useState(null); // { coords }
  const [label, setLabel] = useState("");
  const [crop, setCrop] = useState("wheat");
  const [saving, setSaving] = useState(false);

  // Expose flyTo for the address-search bar above the map.
  const flyTo = (lon, lat) => {
    if (mapRef.current) mapRef.current.flyTo([lat, lon], 14, { duration: 0.8 });
  };

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (containerRef.current._leaflet_id) delete containerRef.current._leaflet_id;
    const map = L.map(containerRef.current, { center: [48.2, 13.8], zoom: 8 });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: "© OpenStreetMap-Mitwirkende",
    }).addTo(map);
    drawnLayer.current = new L.FeatureGroup().addTo(map);
    ownedLayer.current = new L.FeatureGroup().addTo(map);

    const drawControl = new L.Control.Draw({
      draw: {
        polygon: { allowIntersection: false, showArea: true, shapeOptions: { color: "#00FA9A" } },
        polyline: false, rectangle: false, circle: false, marker: false, circlemarker: false,
      },
      edit: { featureGroup: drawnLayer.current, remove: false },
    });
    map.addControl(drawControl);

    map.on(L.Draw.Event.CREATED, (e) => {
      drawnLayer.current.clearLayers();
      drawnLayer.current.addLayer(e.layer);
      const latlngs = e.layer.getLatLngs()[0];
      const coords = latlngs.map((p) => [+p.lng.toFixed(5), +p.lat.toFixed(5)]);
      setPending({ coords });
    });

    mapRef.current = map;
    return () => { try { map.remove(); } catch {} mapRef.current = null; };
  }, []);

  // Render owned fields as read-only overlays.
  useEffect(() => {
    const layer = ownedLayer.current;
    if (!layer) return;
    layer.clearLayers();
    fields.forEach((f) => {
      const latlngs = (f.polygon || []).map(([lon, lat]) => [lat, lon]);
      const active = f.id === selectedId;
      const poly = L.polygon(latlngs, {
        color: active ? "#F5F5F5" : "#7EE8B0",
        weight: active ? 3 : 2,
        fillOpacity: active ? 0.25 : 0.12,
        fillColor: active ? "#00FA9A" : "#7EE8B0",
      });
      poly.bindTooltip(f.label, { permanent: false });
      poly.on("click", () => onSelect(f));
      poly.addTo(layer);
    });
  }, [fields, selectedId, onSelect]);

  const onSave = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      const res = await createMyField({
        label: label.trim() || "Mein Feld",
        crop,
        polygon: pending.coords,
      });
      if (res.ok) {
        toast.success(`Feld gespeichert (${res.area_km2} km²)`);
        drawnLayer.current?.clearLayers();
        setPending(null);
        setLabel("");
        onSaved();
      } else {
        const msg = {
          polygon_too_small: "Polygon zu klein.",
          area_too_large: "Fläche zu groß (max 500 km²).",
          limit_reached: "Limit von 20 Feldern erreicht.",
        }[res.error] || "Konnte Feld nicht speichern.";
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    drawnLayer.current?.clearLayers();
    setPending(null);
    setLabel("");
  };

  return (
    <div className="space-y-3" data-testid="myfields-draw">
      <AddressSearch onPick={flyTo} placeholder="Adresse oder Ort zum Zentrieren suchen…" testid="myfields-address" />
      <div className="eid-card overflow-hidden" style={{ height: 460 }}>
        <div ref={containerRef} style={{ height: "100%", width: "100%" }} data-testid="myfields-map" />
      </div>
      {pending && (
        <div className="eid-card p-4 space-y-2" data-testid="myfields-save-form">
          <div className="eid-label">Neues Feld speichern</div>
          <div className="flex flex-wrap gap-2 items-end">
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="z. B. Hinterer Acker"
              data-testid="myfield-label-input"
              className="flex-1 min-w-[160px] border border-white/10 rounded-sm px-3 py-2 text-sm"
            />
            <select
              value={crop}
              onChange={(e) => setCrop(e.target.value)}
              data-testid="myfield-crop-select"
              className="border border-white/10 rounded-sm px-3 py-2 text-sm bg-[#121212]"
            >
              {CROPS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <button
              onClick={onSave}
              disabled={saving}
              data-testid="myfield-save-btn"
              className="bg-[#00FA9A] text-black hover:bg-[#33FBAE] disabled:opacity-60 rounded-full px-4 py-2 text-sm"
            >
              {saving ? "Speichere…" : "Speichern"}
            </button>
            <button
              onClick={onCancel}
              className="text-zinc-400 hover:text-white text-sm px-3"
              data-testid="myfield-cancel-btn"
            >
              Abbrechen
            </button>
          </div>
          <p className="text-[11px] text-zinc-500">
            Tipp: Das Polygon darf nicht größer als 500 km² sein. Für saubere
            Satellit-Auswertung zeichne möglichst eng um deine Parzelle.
          </p>
        </div>
      )}
    </div>
  );
}

function FieldDetail({ snapshot, field, onRefresh, refreshing }) {
  if (!snapshot) {
    return (
      <div className="eid-card p-5 text-sm text-zinc-400" data-testid="myfield-detail">
        Lade Daten für „{field.label}"…
      </div>
    );
  }
  const m = snapshot.snapshot?.metrics || {};
  const score = regionHealth(m);
  const band = healthBand(score);
  const tone = toneColor(band.level);
  const ns = ndviStatus(m.ndvi_current);
  const ss = soilMoistureStatus(m.soil_moisture_current);
  const ys = yieldStatus(m.yield_forecast_index);
  const cached = snapshot.cached;
  const fetched = snapshot.snapshot?.fetched_at;
  const adv = snapshot.snapshot?.forecast?.advisories || {};

  return (
    <div className="eid-card p-5 space-y-3" data-testid="myfield-detail"
         style={{ borderLeft: `3px solid ${tone.dot}` }}>
      <header className="flex items-start justify-between gap-2">
        <div>
          <div className="eid-label">{field.label}</div>
          <div className="font-mono text-3xl" style={{ color: tone.fg }}>{score}</div>
          <div className="text-xs text-zinc-400">{band.label}</div>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          data-testid="myfield-refresh-btn"
          className="inline-flex items-center gap-1 text-xs border border-white/10 rounded-full px-3 py-1.5 hover:bg-white/5 disabled:opacity-60"
        >
          <ArrowClockwise size={12} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Lade…" : "Aktualisieren"}
        </button>
        <Link
          to={`/field-report/${field.id}`}
          data-testid={`myfield-report-link-${field.id}`}
          className="inline-flex items-center gap-1 text-xs bg-[#00FA9A] text-black hover:bg-[#33FBAE] rounded-full px-3 py-1.5"
        >
          <Printer size={12} /> Jahresbilanz
        </Link>
      </header>

      <div className="grid grid-cols-3 gap-2">
        <Stat label="NDVI" value={m.ndvi_current?.toFixed?.(2) ?? "–"} sub={ns.label} />
        <Stat label="Boden" value={m.soil_moisture_current?.toFixed?.(2) ?? "–"} sub={ss.label} />
        <Stat label="Ertrag" value={m.yield_forecast_index?.toFixed?.(0) ?? "–"} sub={ys.label} />
      </div>

      {adv.irrigation_signal && (
        <div className="p-3 rounded-sm bg-[#0A0A0A] border-l-2 border-[#00FA9A]/40 text-sm">
          <div className="eid-label" style={{ color: "#00FA9A" }}>Prognose 7 Tage</div>
          <ul className="list-disc pl-5 mt-1 space-y-0.5 text-xs">
            <li>Bewässerung: <strong>{labelSig(adv.irrigation_signal)}</strong> · Defizit {adv.irrigation_balance_mm_5d} mm / 5 T</li>
            {adv.frost_days?.length > 0 && <li>Frost-Tage: {adv.frost_days.join(", ")}</li>}
            {adv.blight_signal !== "low" && <li>Krautfäule-Risiko: <strong>{adv.blight_signal}</strong></li>}
            {adv.heatwave_run > 2 && <li>Hitzeperiode: {adv.heatwave_run} Tage</li>}
          </ul>
        </div>
      )}

      <div className="text-[11px] text-zinc-500 font-mono flex justify-between">
        <span>{cached ? "Cache (aktualisierbar)" : "Frisch geladen"}</span>
        <span>{fetched ? new Date(fetched).toLocaleString("de-DE") : ""}</span>
      </div>

      <FieldFructanPanel field={field} />
    </div>
  );
}


/* ------------------------------------------------------------------
 * Hyperlokale Fruktan-Ampel + Alert-Abo für eine eigene Koppel.
 * Ruft das neue /api/myfields/{id}/grassland auf (wird serverseitig aus
 * dem gecachten custom_field_snapshot gerechnet).
 * ------------------------------------------------------------------ */
const FR_META = {
  0: { label: "Niedrig",  color: "#00FA9A", bg: "rgba(0,250,154,0.12)", Icon: CheckCircle, headline: "Weide unbedenklich" },
  1: { label: "Mäßig",    color: "#6B5C2D", bg: "#16140D", Icon: Warning,     headline: "Leicht erhöhtes Risiko" },
  2: { label: "Erhöht",   color: "#FFC24B", bg: "rgba(255,215,0,0.10)", Icon: Warning,     headline: "Fruktan-Risiko erhöht" },
  3: { label: "Kritisch", color: "#FF5500", bg: "rgba(255,85,0,0.12)", Icon: Fire,        headline: "Hufrehe-Alarm" },
};

function FieldFructanPanel({ field }) {
  const [analysis, setAnalysis] = useState(null);
  const [err, setErr] = useState(null);
  const [emailState, setEmailState] = useState("idle");
  const [pushState, setPushState] = useState("idle");
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    let cancelled = false;
    setAnalysis(null); setErr(null);
    fetchMyFieldGrassland(field.id)
      .then((d) => { if (!cancelled) setAnalysis(d); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Analyse fehlgeschlagen"); });
    return () => { cancelled = true; };
  }, [field.id]);

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailState("loading");
    try {
      await subscribeFieldAlert(field.id, { email: email.trim() });
      setEmailState("done");
      toast.success("Bestätigungsmail gesendet. Bitte Postfach prüfen.");
    } catch (err) {
      setEmailState("idle");
      toast.error(err?.response?.data?.detail || "Anmeldung fehlgeschlagen");
    }
  };

  const enablePush = async () => {
    setPushState("loading");
    try {
      // Subscribe in the browser then POST the subscription to our field endpoint.
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        throw new Error("Browser unterstützt keine Push-Benachrichtigungen");
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") throw new Error("Push-Berechtigung verweigert");
      const reg = await navigator.serviceWorker.ready;
      // Fetch VAPID via the same helper as region push
      const vapidRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/vapid-key`);
      const { public_key } = await vapidRes.json();
      const raw = atob((public_key + "==").slice(0, ((public_key.length + 3) >> 2) << 2).replace(/-/g, "+").replace(/_/g, "/"));
      const appKey = new Uint8Array([...raw].map((c) => c.charCodeAt(0)));
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
      await subscribeFieldAlert(field.id, { push_subscription: sub.toJSON() });
      setPushState("done");
      toast.success("Browser-Push aktiviert für " + field.label);
    } catch (err) {
      setPushState("idle");
      toast.error(err?.message || "Push fehlgeschlagen");
    }
  };

  if (err) {
    return (
      <div className="eid-card p-3 text-xs text-zinc-500" data-testid="myfield-fructan-error">
        Fruktan-Analyse zurzeit nicht verfügbar: {err}
      </div>
    );
  }
  if (!analysis) {
    return (
      <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="myfield-fructan-loading">
        <CircleNotch size={12} className="animate-spin" /> Berechne Fruktan-Ampel für diese Koppel…
      </div>
    );
  }

  const score = analysis.fructan_risk?.score ?? 0;
  const meta = FR_META[score];
  const Icon = meta.Icon;
  const horseTips = analysis.animals?.horse?.messages || [];

  return (
    <div
      className="rounded-sm overflow-hidden border border-white/10"
      data-testid="myfield-fructan-panel"
    >
      <div className="flex items-start gap-3 p-3" style={{ background: meta.bg }}>
        <span className="shrink-0 p-2 rounded-sm bg-black/60" style={{ color: meta.color }}>
          <Icon size={20} weight="fill" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>
            <Horse size={11} /> Fruktan-Ampel für diese Koppel · Stufe {score}/3
          </div>
          <div className="text-sm font-medium mt-0.5" style={{ color: meta.color }}>
            {meta.headline} · {meta.label}
          </div>
          {horseTips[0] && (
            <div className="text-xs text-white mt-1 leading-snug">{horseTips[0]}</div>
          )}
          {analysis.fructan_risk?.reasons?.length > 0 && score >= 1 && (
            <details className="text-[11px] mt-1" data-testid="myfield-fructan-reasons">
              <summary className="cursor-pointer text-zinc-400 hover:text-white">Warum?</summary>
              <ul className="mt-1 ml-3 text-zinc-400 space-y-0.5">
                {analysis.fructan_risk.reasons.map((r, i) => <li key={i}>– {r}</li>)}
              </ul>
            </details>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 border-t border-white/10">
        <Cell label="Protein" value={analysis.nutrition?.crude_protein_pct} unit="% TM" />
        <Cell label="NEL" value={analysis.nutrition?.nel_mj_per_kg_dm} unit="MJ/kg" />
        <Cell label="Aufwuchs" value={analysis.nutrition?.biomass_index} unit="/100" />
      </div>

      <div className="p-3 border-t border-white/10 bg-[#0A0A0A]">
        {!showSubscribe ? (
          <button
            onClick={() => setShowSubscribe(true)}
            data-testid="myfield-subscribe-toggle"
            className="w-full inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-[#3E3E3E] text-white rounded-sm px-3 py-2 text-xs font-medium"
          >
            <Bell size={14} /> Täglich benachrichtigen (Mail + Push)
          </button>
        ) : (
          <div className="space-y-2" data-testid="myfield-subscribe-box">
            <form onSubmit={submitEmail} className="flex gap-1.5">
              <div className="relative flex-1">
                <EnvelopeSimple size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="deine@email.at"
                  disabled={emailState !== "idle"}
                  data-testid="myfield-email-input"
                  className="w-full pl-8 pr-2 py-2 text-sm bg-[#121212] border border-white/10 rounded-sm focus:outline-none focus:border-[#00FA9A]/40"
                />
              </div>
              <button
                type="submit"
                disabled={emailState !== "idle"}
                data-testid="myfield-email-submit"
                className="px-3 py-2 text-xs font-medium bg-[#00FA9A] hover:bg-[#33FBAE] text-white rounded-sm disabled:opacity-50 inline-flex items-center gap-1"
              >
                {emailState === "loading" ? <CircleNotch size={12} className="animate-spin" />
                  : emailState === "done" ? <CheckCircle size={12} weight="fill" />
                  : <PaperPlaneTilt size={12} />}
                {emailState === "done" ? "Geprüft" : "Per Mail"}
              </button>
            </form>
            <button
              onClick={enablePush}
              disabled={pushState !== "idle"}
              data-testid="myfield-push-enable"
              className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-2 bg-white/10 hover:bg-[#3E3E3E] text-white rounded-sm text-xs font-medium disabled:opacity-50"
            >
              {pushState === "loading" ? <CircleNotch size={14} className="animate-spin" />
                : pushState === "done" ? <CheckCircle size={14} weight="fill" />
                : <Bell size={14} weight="fill" />}
              {pushState === "done" ? "Push aktiv" : "Browser-Push aktivieren"}
            </button>
            <p className="text-[10px] text-zinc-500 leading-snug">
              Nur bei Fruktan-Stufe ≥ 2 oder wenn sich die Stufe ändert. Jederzeit widerrufbar.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Cell({ label, value, unit }) {
  return (
    <div className="py-2 text-center border-r last:border-r-0 border-white/10">
      <div className="text-[9px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm font-mono font-medium">
        {value ?? "–"}<span className="text-[9px] text-zinc-500 ml-1">{unit}</span>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="border border-white/10 rounded-sm p-2 text-center">
      <div className="eid-label">{label}</div>
      <div className="font-mono text-lg mt-0.5">{value}</div>
      <div className="text-[10px] text-zinc-500">{sub}</div>
    </div>
  );
}

function labelSig(s) {
  return ({ urgent: "Dringend", plan: "Planen", ok: "OK" }[s] || "—");
}
